import { Bike } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { IMG } from "../data/content";

const ImageTile = ({ img, alt, title, text, tag, span, testid }) => (
    <Reveal className={span}>
        <figure
            data-testid={testid}
            className="group relative h-full min-h-[280px] w-full overflow-hidden rounded-2xl"
        >
            <img
                src={img}
                alt={alt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
                loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-heritage-ink/85 via-heritage-ink/20 to-transparent transition-opacity duration-500" />
            <figcaption className="absolute inset-x-0 bottom-0 p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-heritage-gold">
                    {tag}
                </p>
                <h3 className="mt-2 font-serif text-2xl font-semibold text-heritage-parchment md:text-3xl">
                    {title}
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-heritage-parchment/70 opacity-0 transition-all duration-500 group-hover:opacity-100 md:translate-y-2 md:group-hover:translate-y-0">
                    {text}
                </p>
            </figcaption>
        </figure>
    </Reveal>
);

const Collections = () => (
    <section
        id="collections"
        data-testid="collections-bento-section"
        className="ihcc-grain relative bg-heritage-ink py-24 md:py-36"
    >
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10">
            <Reveal className="flex flex-wrap items-end justify-between gap-6">
                <div className="max-w-2xl">
                    <Eyebrow dark>The Collection</Eyebrow>
                    <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-heritage-parchment sm:text-4xl lg:text-5xl">
                        Every Object Has a <em className="text-heritage-gold">Story</em>
                    </h2>
                </div>
                <p className="max-w-sm text-sm leading-relaxed text-heritage-parchment/60">
                    Heritage collectibles, vintage artefacts and culturally significant objects —
                    fragments of everyday history, kept alive in George Town.
                </p>
            </Reveal>

            <div
                data-testid="collections-bento-grid"
                className="mt-16 grid grid-cols-1 gap-6 md:auto-rows-[240px] md:grid-cols-12"
            >
                <ImageTile
                    span="md:col-span-8 md:row-span-2"
                    testid="collection-tile-artefacts"
                    img={IMG.artefacts}
                    alt="Heritage artefacts at IHCC Penang — rare Indian artefacts, vintage lamps and carved wooden objects on display"
                    tag="Fig. 01 · The Heritage Rooms"
                    title="Heritage Artefacts"
                    text="Rare artefacts, vintage lamps and carved objects — fragments of everyday history and craftsmanship, conserved with care."
                />
                <ImageTile
                    span="md:col-span-4"
                    testid="collection-tile-memorabilia"
                    img={IMG.shop}
                    alt="Vintage memorabilia at the IHCC vintage shop in George Town — collected treasures, souvenirs and traditional items"
                    tag="Fig. 02 · The Vintage Shop"
                    title="Vintage Memorabilia"
                    text="A trove of vintage treasures, souvenirs and traditional items to browse — and a piece of heritage to take home."
                />
                <Reveal className="md:col-span-4">
                    <div
                        data-testid="collection-tile-scooters"
                        className="group relative flex h-full min-h-[240px] flex-col justify-between overflow-hidden rounded-2xl bg-heritage-terra p-7 transition-colors duration-500 hover:bg-heritage-vermilion"
                    >
                        <Bike
                            size={30}
                            strokeWidth={1.4}
                            className="text-heritage-sand/90 transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110"
                        />
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-heritage-sand/70">
                                Fig. 03 · On Two Wheels
                            </p>
                            <h3 className="mt-2 font-serif text-2xl font-semibold text-heritage-parchment">
                                Vintage Scooters &amp; Two-Wheelers
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-heritage-sand/80">
                                Classic scooters and two-wheelers from Penang's streets — icons of
                                everyday mobility, preserved as living history.
                            </p>
                        </div>
                    </div>
                </Reveal>
                <ImageTile
                    span="md:col-span-6"
                    testid="collection-tile-traditional"
                    img={IMG.nagarathar}
                    alt="Traditional cultural objects at IHCC Penang — an ornate chandelier glowing above the Nagarathar heritage room"
                    tag="Fig. 04 · Nagarathar Heritage"
                    title="Traditional Cultural Objects"
                    text="Nagarathar heritage, traditional attire and cultural treasures — a proud legacy presented with reverence."
                />
                <ImageTile
                    span="md:col-span-6"
                    testid="collection-tile-everyday"
                    img={IMG.technology}
                    alt="Everyday historical objects at IHCC Penang — a vintage treadle sewing machine, hand grinder and ceiling fan"
                    tag="Fig. 05 · Another Era"
                    title="Everyday Historical Objects"
                    text="Treadle sewing machines, hand grinders and the quiet ingenuity of daily life, decades ago."
                />
            </div>
        </div>
    </section>
);

export default Collections;
